import React, { createContext, useContext, useState, useEffect } from 'react';
import { getConfiguration } from '../services/api';

// Define the configuration structure
interface SiteSettings {
  mainHeading: {
    type: string;
    textContent: string;
    imageUrl: string;
  };
  tagline: string;
  description: string;
  callToAction: {
    text: string;
    buttonText: string;
  };
  launchInfo: string;
  colors: {
    backgroundColor: string;
    textColor: string;
    accentColor: string;
  };
  fonts: {
    headingFont: string;
    bodyFont: string;
  };
  dualCTASettings: {
    enabled: boolean;
    animation: {
      breathingEnabled: boolean;
      breathingInterval: number;
      easingFunction: string;
      syncWithVanta: boolean;
    };
    left: {
      type: 'external';
      title: string;
      subtitle: string;
      buttonText: string;
      icon?: string;
      particleAttraction: boolean;
      action: {
        type: 'external_link';
        url: string;
        openInNewTab: boolean;
      };
      position: {
        desktop: { x: string; y: string; };
        mobile: { order: number; };
      };
    };
    right: {
      type: 'blog' | 'external';
      title: string;
      subtitle: string;
      buttonText: string;
      icon?: string;
      particleAttraction: boolean;
      action: {
        type: 'external_link';
        url: string;
        openInNewTab: boolean;
      };
      position: {
        desktop: { x: string; y: string; };
        mobile: { order: number; };
      };
    };
    harmony: {
      colorSyncEnabled: boolean;
      morphingShapes: boolean;
      glowIntensity: number;
      pulseSync: 'independent' | 'alternating' | 'synchronized';
    };
  };
}

interface FooterSettings {
  socialLinks: {
    x: string;
    instagram: string;
    tiktok: string;
    facebook: string;
    youtube: string;
    linkedin: string;
  };
  legalInfo: {
    copyright: string;
    privacyPolicy: string;
    termsConditions: string;
    disclaimer: string;
  };
}

interface VantaSettings {
  mouseControls: boolean;
  touchControls: boolean;
  gyroControls: boolean;
  minHeight: number;
  minWidth: number;
  scale: number;
  scaleMobile: number;
  backgroundColor: string;
  birdSize: number;
  wingSpan: number;
  speedLimit: number;
  separation: number;
  alignment: number;
  cohesion: number;
  quantity: number;
}

interface SeoSettings {
  metaTitle: string;
  metaDescription: string;
  ogImage: string;
  structuredData: any;
}

interface Configuration {
  siteSettings: SiteSettings;
  footerSettings: FooterSettings;
  vantaSettings: VantaSettings;
  seoSettings: SeoSettings;
  lastUpdated?: any;
  updatedBy?: string;
}

// Get initial configuration from environment variables
const getInitialConfig = (): Configuration => {
  return {
    siteSettings: {
      mainHeading: {
        type: process.env.REACT_APP_SITE_HEADING_TYPE || 'text',
        textContent: process.env.REACT_APP_SITE_HEADING_TEXT || 'Coming Soon',
        imageUrl: process.env.REACT_APP_SITE_HEADING_IMAGE || ''
      },
      tagline: process.env.REACT_APP_SITE_TAGLINE || '',
      description: process.env.REACT_APP_SITE_DESCRIPTION || '',
      callToAction: {
        text: process.env.REACT_APP_SITE_CTA_TEXT || '',
        buttonText: process.env.REACT_APP_SITE_CTA_BUTTON || 'Subscribe'
      },
      launchInfo: process.env.REACT_APP_SITE_LAUNCH_INFO || '',
      colors: {
        backgroundColor: process.env.REACT_APP_BACKGROUND_COLOR || '#ffffff',
        textColor: process.env.REACT_APP_TEXT_COLOR || '#000000',
        accentColor: process.env.REACT_APP_ACCENT_COLOR || '#4255a4'
      },
      fonts: {
        headingFont: process.env.REACT_APP_HEADING_FONT || 'sans-serif',
        bodyFont: process.env.REACT_APP_BODY_FONT || 'sans-serif'
      },
      dualCTASettings: {
        enabled: process.env.REACT_APP_DUAL_CTA_ENABLED !== 'false',
        animation: {
          breathingEnabled: process.env.REACT_APP_BREATHING_ENABLED !== 'false',
          breathingInterval: Number(process.env.REACT_APP_BREATHING_INTERVAL || 5000),
          easingFunction: process.env.REACT_APP_EASING_FUNCTION || 'cubic-bezier(0.4, 0, 0.6, 1)',
          syncWithVanta: process.env.REACT_APP_PARTICLE_SYNC_ENABLED !== 'false'
        },
        left: {
          type: 'external' as const,
          title: process.env.REACT_APP_LEFT_CTA_TITLE || 'Share Your Requests',
          subtitle: process.env.REACT_APP_LEFT_CTA_SUBTITLE || 'Visit our Ideas Portal to submit ideas, vote on polls, and shape content direction',
          buttonText: process.env.REACT_APP_LEFT_CTA_BUTTON || 'Open Ideas Portal',
          particleAttraction: process.env.REACT_APP_LEFT_CTA_ATTRACTION !== 'false',
          action: {
            type: 'external_link' as const,
            url: process.env.REACT_APP_IDEAS_PORTAL_URL || 'https://ideas.tobybrooks.com',
            openInNewTab: process.env.REACT_APP_IDEAS_PORTAL_NEW_TAB !== 'false'
          },
          position: {
            desktop: {
              x: process.env.REACT_APP_LEFT_CTA_X || '25%',
              y: process.env.REACT_APP_LEFT_CTA_Y || '5%'
            },
            mobile: { order: Number(process.env.REACT_APP_LEFT_CTA_MOBILE_ORDER || 2) }
          }
        },
        right: {
          type: 'blog' as const,
          title: process.env.REACT_APP_RIGHT_CTA_TITLE || 'Explore Insights',
          subtitle: process.env.REACT_APP_RIGHT_CTA_SUBTITLE || 'Dive deep into Toby\'s thoughts and discoveries',
          buttonText: process.env.REACT_APP_RIGHT_CTA_BUTTON || 'Visit Blog',
          particleAttraction: process.env.REACT_APP_RIGHT_CTA_ATTRACTION !== 'false',
          action: {
            type: 'external_link' as const,
            url: process.env.REACT_APP_BLOG_URL || 'https://blog.tobybrooks.com',
            openInNewTab: process.env.REACT_APP_BLOG_NEW_TAB !== 'false'
          },
          position: {
            desktop: {
              x: process.env.REACT_APP_RIGHT_CTA_X || '75%',
              y: process.env.REACT_APP_RIGHT_CTA_Y || '5%'
            },
            mobile: { order: Number(process.env.REACT_APP_RIGHT_CTA_MOBILE_ORDER || 3) }
          }
        },
        harmony: {
          colorSyncEnabled: process.env.REACT_APP_COLOR_SYNC_ENABLED !== 'false',
          morphingShapes: process.env.REACT_APP_MORPHING_SHAPES === 'true',
          glowIntensity: Number(process.env.REACT_APP_CTA_GLOW_INTENSITY || 0.6),
          pulseSync: (process.env.REACT_APP_PULSE_SYNC as 'independent' | 'alternating' | 'synchronized') || 'alternating'
        }
      }
    },
    footerSettings: {
      socialLinks: {
        x: process.env.REACT_APP_SOCIAL_X || '',
        instagram: process.env.REACT_APP_SOCIAL_INSTAGRAM || '',
        tiktok: process.env.REACT_APP_SOCIAL_TIKTOK || '',
        facebook: process.env.REACT_APP_SOCIAL_FACEBOOK || '',
        youtube: process.env.REACT_APP_SOCIAL_YOUTUBE || '',
        linkedin: process.env.REACT_APP_SOCIAL_LINKEDIN || ''
      },
      legalInfo: {
        copyright: process.env.REACT_APP_COPYRIGHT || '',
        privacyPolicy: process.env.REACT_APP_PRIVACY_POLICY || '',
        termsConditions: process.env.REACT_APP_TERMS_CONDITIONS || '',
        disclaimer: process.env.REACT_APP_DISCLAIMER || ''
      }
    },
    vantaSettings: {
      mouseControls: process.env.REACT_APP_VANTA_MOUSE_CONTROLS === 'true',
      touchControls: process.env.REACT_APP_VANTA_TOUCH_CONTROLS === 'true',
      gyroControls: process.env.REACT_APP_VANTA_GYRO_CONTROLS === 'true',
      minHeight: Number(process.env.REACT_APP_VANTA_MIN_HEIGHT || 200),
      minWidth: Number(process.env.REACT_APP_VANTA_MIN_WIDTH || 200),
      scale: Number(process.env.REACT_APP_VANTA_SCALE || 1.00),
      scaleMobile: Number(process.env.REACT_APP_VANTA_SCALE_MOBILE || 1.00),
      backgroundColor: process.env.REACT_APP_VANTA_BACKGROUND_COLOR || '0x4255a4',
      birdSize: Number(process.env.REACT_APP_VANTA_BIRD_SIZE || 1.00),
      wingSpan: Number(process.env.REACT_APP_VANTA_WING_SPAN || 30.00),
      speedLimit: Number(process.env.REACT_APP_VANTA_SPEED_LIMIT || 5.00),
      separation: Number(process.env.REACT_APP_VANTA_SEPARATION || 20.00),
      alignment: Number(process.env.REACT_APP_VANTA_ALIGNMENT || 20.00),
      cohesion: Number(process.env.REACT_APP_VANTA_COHESION || 20.00),
      quantity: Number(process.env.REACT_APP_VANTA_QUANTITY || 5.00)
    },
    seoSettings: {
      metaTitle: process.env.REACT_APP_META_TITLE || 'Coming Soon',
      metaDescription: process.env.REACT_APP_META_DESCRIPTION || 'Our new website is on its way',
      ogImage: process.env.REACT_APP_OG_IMAGE || '',
      structuredData: {}
    }
  };
};

interface ConfigContextType {
  config: Configuration;
  loading: boolean;
  error: Error | null;
  updateConfig: (newConfig: Partial<Configuration>) => void;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function useConfig() {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<Configuration>(getInitialConfig());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const dbConfig = await getConfiguration();
        
        if (dbConfig) {
          // Merge database config with initial config (for any missing fields)
          setConfig(prevConfig => ({
            ...prevConfig,
            ...dbConfig
          }));
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading configuration:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setLoading(false);
      }
    };
    
    loadConfiguration();
  }, []);

  const updateConfig = (newConfig: Partial<Configuration>) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      ...newConfig
    }));
  };

  const value = {
    config,
    loading,
    error,
    updateConfig
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}